import React from 'react';
import { ChevronDown, ChevronUp, Wrench, Mic, Camera, Monitor } from 'lucide-react';
import { cn } from '../lib/utils';
import { Input } from './ui/input';
import { Label } from './ui/label';

const EQUIPO_IMAGES = {
  microfono: 'https://images.unsplash.com/photo-1558811916-51c8d56d29c6?w=120&h=80&fit=crop',
  camara: 'https://images.unsplash.com/photo-1669050609796-e47bba648762?w=120&h=80&fit=crop',
  lavalier: 'https://images.unsplash.com/photo-1722681261218-c9d7272815f0?w=120&h=80&fit=crop',
  teleprompter: 'https://images.unsplash.com/photo-1757845701047-69080f6b1139?w=120&h=80&fit=crop',
};

function CollapsibleSection({ title, icon: Icon, isOpen, onToggle, children, badge }) {
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-4 bg-[#0f133a]/80 hover:bg-[#1a1f50]/80 transition-colors" data-testid="btn-toggle-renta-equipo">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-gradient-to-br from-[#aa2aff] to-[#7012b2]">
            <Icon className="w-5 h-5 text-white" />
          </div>
          <span className="font-semibold text-white text-lg">{title}</span>
          {badge && <span className="px-2 py-0.5 bg-[#aa2aff] text-white text-xs rounded-full">{badge}</span>}
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>
      {isOpen && <div className="p-4 bg-[#080c2d]/50">{children}</div>}
    </div>
  );
}

function EquipoItem({ icon: Icon, image, label, description, unitPrice, unitLabel, value, onChange, formatPrice, currency, testId, showQty2, qty2Value, onQty2Change, qty2Label }) {
  const total = showQty2 ? unitPrice * (value || 0) * (qty2Value || 0) : unitPrice * (value || 0);
  return (
    <div className={cn("p-3 rounded-lg border transition-all", value > 0 ? "border-[#aa2aff] bg-[#aa2aff]/10" : "border-white/10 bg-white/5")}>
      <div className="flex items-start gap-3">
        {/* Thumbnail */}
        {image && (
          <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0 border border-white/10">
            <img src={image} alt={label} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-[#aa2aff] flex-shrink-0" />
            <span className="text-white text-sm font-medium">{label}</span>
          </div>
          {description && <p className="text-slate-400 text-xs mt-0.5">{description}</p>}
          <p className="text-slate-400 text-xs mt-0.5">{formatPrice(unitPrice)} {currency} / {unitLabel}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {showQty2 && (
            <div className="text-center">
              <Label className="text-slate-500 text-[10px]">{qty2Label || 'Cant.'}</Label>
              <Input type="number" min="0" max="50" value={qty2Value || ''} onChange={(e) => onQty2Change(parseInt(e.target.value) || 0)}
                className="bg-[#080c2d]/50 border-[#272b96] text-white w-16 h-8 text-sm focus:border-[#aa2aff]" data-testid={`${testId}-qty2`} />
            </div>
          )}
          <div className="text-center">
            <Label className="text-slate-500 text-[10px]">{showQty2 ? 'Días' : 'Días'}</Label>
            <Input type="number" min="0" max="99" value={value || ''} onChange={(e) => onChange(parseInt(e.target.value) || 0)}
              className="bg-[#080c2d]/50 border-[#272b96] text-white w-16 h-8 text-sm focus:border-[#aa2aff]" data-testid={testId} />
          </div>
        </div>
      </div>
      {total > 0 && <p className="text-[#aa2aff] text-xs font-semibold mt-2 ml-[76px]">= {formatPrice(total)} {currency}</p>}
    </div>
  );
}

export function RentaEquipoSection({ isOpen, onToggle, data = {}, onChange, formatPrice, currency, settings = {} }) {
  const prices = {
    microfono: settings.price_renta_microfono || 497,
    camara: settings.price_renta_camara || 1850,
    lavalier: settings.price_renta_lavalier || 750,
    teleprompter: settings.price_renta_teleprompter || 897,
  };

  const micTotal = prices.microfono * (data.microfonoQty || 0) * (data.microfonoDias || 0);
  const camTotal = prices.camara * (data.camaraDias || 0);
  const lavTotal = prices.lavalier * (data.lavalierQty || 0) * (data.lavalierDias || 0);
  const teleTotal = prices.teleprompter * (data.teleprompterDias || 0);
  const subtotal = micTotal + camTotal + lavTotal + teleTotal;

  const handleChange = (field, value) => onChange({ ...data, [field]: value });
  const badge = subtotal > 0 ? formatPrice(subtotal) : null;

  return (
    <CollapsibleSection title="Renta de Equipo" icon={Wrench} isOpen={isOpen} onToggle={onToggle} badge={badge}>
      <div className="space-y-3">
        <EquipoItem icon={Mic} image={EQUIPO_IMAGES.microfono} label="Micrófono Shure M7X / SM7B" description="Micrófono profesional de estudio" unitPrice={prices.microfono} unitLabel="unidad/día"
          value={data.microfonoDias || 0} onChange={(v) => handleChange('microfonoDias', v)} showQty2 qty2Value={data.microfonoQty || 0} onQty2Change={(v) => handleChange('microfonoQty', v)} qty2Label="Micrófonos"
          formatPrice={formatPrice} currency={currency} testId="renta-microfono" />
        <EquipoItem icon={Camera} image={EQUIPO_IMAGES.camara} label="Cámara Adicional Canon MR10" description="Canon MR10 o similar. Incluye trípode y lente" unitPrice={prices.camara} unitLabel="día"
          value={data.camaraDias || 0} onChange={(v) => handleChange('camaraDias', v)}
          formatPrice={formatPrice} currency={currency} testId="renta-camara" />
        <EquipoItem icon={Mic} image={EQUIPO_IMAGES.lavalier} label="Micrófono Lavalier Sennheiser" unitPrice={prices.lavalier} unitLabel="unidad/día"
          value={data.lavalierDias || 0} onChange={(v) => handleChange('lavalierDias', v)} showQty2 qty2Value={data.lavalierQty || 0} onQty2Change={(v) => handleChange('lavalierQty', v)} qty2Label="Lavaliers"
          formatPrice={formatPrice} currency={currency} testId="renta-lavalier" />
        <EquipoItem icon={Monitor} image={EQUIPO_IMAGES.teleprompter} label="Teleprompter" unitPrice={prices.teleprompter} unitLabel="día"
          value={data.teleprompterDias || 0} onChange={(v) => handleChange('teleprompterDias', v)}
          formatPrice={formatPrice} currency={currency} testId="renta-teleprompter" />

        {subtotal > 0 && (
          <div className="pt-3 border-t border-white/10 flex justify-between items-center">
            <span className="text-white font-semibold">Subtotal Renta de Equipo:</span>
            <span className="text-[#aa2aff] font-bold text-lg">{formatPrice(subtotal)} {currency}</span>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
}

export function calcRentaEquipoTotal(data = {}, settings = {}) {
  const p = { mic: settings.price_renta_microfono || 497, cam: settings.price_renta_camara || 1850, lav: settings.price_renta_lavalier || 750, tel: settings.price_renta_teleprompter || 897 };
  return p.mic * (data.microfonoQty || 0) * (data.microfonoDias || 0) + p.cam * (data.camaraDias || 0) + p.lav * (data.lavalierQty || 0) * (data.lavalierDias || 0) + p.tel * (data.teleprompterDias || 0);
}
